import { lazy, Suspense } from "react";
import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

/**
 * Bundle boundary for the Checks (evidence) surface.
 *
 * WHY. `evidence-surface.tsx` (90,751 source bytes) and its co-located
 * `evidence-surface-copy.ts` (59,428 source bytes, 5 locales) were both in the
 * initial Studio chunk, because `IDEShellLayout` and `essentials-release-
 * surface` imported the component statically. Neither renders it at first
 * paint: `ActiveSurface` is a switch over `activeRailItem` and mounts exactly
 * one surface, and the release stepper mounts it only on its `review` step.
 * The bytes were paid by every session and read by the ones that open Checks.
 *
 * WHY THIS SHAPE RATHER THAN AN ASYNC COPY ACCESSOR. `getEvidenceSurfaceCopy`
 * is read DURING RENDER — `EvidenceSurface` calls it in its body and
 * `getEvidenceSurfacePresentation` calls it for the header. Making the
 * accessor async would paint the build-default locale and swap it, which is
 * the flash that blocked the general case for every co-located copy table
 * (see the `largestJavaScriptBytes` note in scripts/studio-bundle-budget.mjs).
 * Deferring the whole SURFACE has no such frame: nothing of it is on screen
 * before the chunk lands, so the placeholder below is the only thing the
 * reader sees, and it is localized from an eager table.
 *
 * Same shape, same reasoning, as `AsIsView` in `IDEShellLayout.tsx`.
 *
 * The lazy type is created ONCE at module scope. Recreating it inside a
 * component makes React treat every render as a new lazy component and
 * repaint the Suspense fallback — the trap `App.tsx` documents for its route
 * elements.
 */
const EvidenceSurfaceChunk = lazy(() =>
  import("./evidence-surface").then((m) => ({ default: m.EvidenceSurface })),
);

/**
 * The wait state for the Checks chunk.
 *
 * These five strings CANNOT live in `evidence-surface-copy.ts`: that module is
 * the larger half of what this boundary defers, so importing it here would
 * pull it straight back into the initial chunk and recover nothing. They are
 * deliberately the only Checks literals that stay eager, and they are the
 * generic "loading" register rather than any of the surface's own framing, so
 * they cannot drift against the three presentations the surface chooses
 * between once it mounts.
 */
const LOADING_COPY: Record<LocaleCode, string> = {
  en: "Loading checks…",
  ko: "검사를 불러오는 중…",
  ja: "検査を読み込んでいます…",
  zh: "正在加载检查…",
  "zh-TW": "正在載入檢查…",
};

/** Exported for the test that pins the wait state in every locale. */
export function EvidenceSurfaceLoadingPlaceholder() {
  const { locale } = useStudioLocale();
  return (
    <div
      data-testid="evidence-surface-loading-placeholder"
      className="flex h-full items-center justify-center text-sm text-muted-foreground"
    >
      {LOADING_COPY[locale] ?? LOADING_COPY.en}
    </div>
  );
}

/**
 * Drop-in replacement for a static `<EvidenceSurface />`.
 *
 * The Suspense boundary lives here rather than at the two call sites so that
 * both of them get the same wait state, and so a third call site cannot
 * reintroduce a static import by accident.
 */
export function EvidenceSurfaceLazy() {
  return (
    <Suspense fallback={<EvidenceSurfaceLoadingPlaceholder />}>
      <EvidenceSurfaceChunk />
    </Suspense>
  );
}
